import React, { useEffect, useState } from 'react';
import { CheckCircle2, Circle, ArrowRight, X, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../realtime/RealtimeContext';
import api from '../api/client';

// First-run setup checklist — only for admins, only while the app is still empty.
export default function GettingStarted() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('kmt_gs_dismissed') === '1');

  const isAdmin = user?.role === 'ADMIN';

  const load = () => {
    if (!isAdmin) return;
    Promise.all([
      api.listMachines().catch(() => []),
      api.listUsers().catch(() => []),
      api.getShifts().catch(() => []),
    ]).then(([machines, users, shifts]) => {
      setCounts({
        machines: machines.length,
        staff: users.filter((u) => u.role && u.role !== 'ADMIN').length,
        shifts: shifts.length,
      });
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);
  useRealtime(() => load());

  if (!isAdmin || dismissed || !counts) return null;

  const steps = [
    { key: 'machines', label: 'Add your machines', desc: 'Register the machines and lines on your shop floor', done: counts.machines > 0, to: '/machines' },
    { key: 'staff', label: 'Add your staff', desc: 'Invite supervisors & operators (or approve Google sign-ins)', done: counts.staff > 0, to: '/users' },
    { key: 'shifts', label: 'Set your shift timings', desc: 'Confirm the Day / Evening / Night windows in Settings', done: counts.shifts > 0, to: '/settings' },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null; // fully set up — hide automatically

  const dismiss = () => { localStorage.setItem('kmt_gs_dismissed', '1'); setDismissed(true); };

  return (
    <div className="kmt-panel p-6 mb-6 kmt-fade-in" data-testid="getting-started" style={{ borderColor: 'var(--kmt-gold)' }}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--kmt-gold)', color: '#17140a' }}><Rocket size={20} /></div>
          <div>
            <div className="font-display font-semibold text-lg">Welcome — let's set up your factory</div>
            <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{doneCount} of {steps.length} done · finish these to get your shop floor live</div>
          </div>
        </div>
        <button onClick={dismiss} data-testid="getting-started-dismiss" style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-text)]" title="Dismiss"><X size={18} /></button>
      </div>

      <div className="h-1.5 rounded-full mt-4" style={{ background: '#0e0e10' }}>
        <div className="h-1.5 rounded-full" style={{ width: `${(doneCount / steps.length) * 100}%`, background: 'var(--kmt-gold)', transition: 'width 0.4s ease' }} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        {steps.map((s) => (
          <button key={s.key} onClick={() => navigate(s.to)} data-testid={`gs-step-${s.key}`}
            className="text-left p-4 rounded-xl transition-colors"
            style={{ background: 'var(--kmt-panel-2)', border: `1px solid ${s.done ? 'var(--kmt-green)' : 'var(--kmt-border)'}` }}>
            <div className="flex items-center gap-2">
              {s.done
                ? <CheckCircle2 size={18} style={{ color: 'var(--kmt-green)' }} />
                : <Circle size={18} style={{ color: 'var(--kmt-muted-2)' }} />}
              <span className="font-medium" style={{ textDecoration: s.done ? 'line-through' : 'none', color: s.done ? 'var(--kmt-muted)' : 'var(--kmt-text)' }}>{s.label}</span>
            </div>
            <div className="text-xs mt-1.5" style={{ color: 'var(--kmt-muted)' }}>{s.desc}</div>
            {!s.done && <div className="flex items-center gap-1 text-xs mt-2" style={{ color: 'var(--kmt-gold)' }}>Start <ArrowRight size={12} /></div>}
          </button>
        ))}
      </div>
    </div>
  );
}
